// @flow

import * as React from 'react'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import {
  Button,
  CardActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TextField,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
} from '@material-ui/core'

import { formatDate } from '../../../utils/dateFormatter'
import type { Claim } from '../Types'

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    table: {
      minWidth: 900,
    },
    cell: {
      fontSize: '1.5rem',
    },
    input: {
      fontSize: '1.5rem',
    },
    dateCell: {
      whiteSpace: 'nowrap',
      width: 150,
      fontSize: '1.5rem',
    },
  })
)
type Props = {
  records: Array<Claim>,
  province: string,
  onSelectedCallback: (Array<Claim>) => void,
  onEditingClaims: (Array<Claim>) => void,
  handleCancel: () => void,
  handleNext: () => void,
}

const headCells = [
  { id: 'status', numeric: false, disablePadding: true, label: 'Status' },
  {
    id: 'dataOfService',
    numeric: true,
    disablePadding: false,
    label: 'Date of service',
  },
  { id: 'code', numeric: true, disablePadding: false, label: 'Code' },
  {
    id: 'description',
    numeric: false,
    disablePadding: false,
    label: 'Description',
  },
  {
    id: 'privateBillingAmount',
    numeric: true,
    disablePadding: false,
    label: 'Amount',
  },
]

const ministryMapping: { [string]: string } = {
  BC: 'BCMA',
  ON: 'OHIP',
  AB: 'AHCIP',
}

function ClaimsList({
  records,
  province,
  onSelectedCallback,
  onEditingClaims,
  handleCancel,
  handleNext,
}: Props): React.Element<'div'> {
  const classes = useStyles()
  const [selectedClaims, setSelectedClaims] = React.useState([])
  const [claims, setClaims] = React.useState([])

  function handleSelectAllClick(event: SyntheticInputEvent<HTMLInputElement>): void {
    let newSelecteds = []

    if (event.target.checked) newSelecteds = records

    setSelectedClaims(newSelecteds)
    onSelectedCallback(newSelecteds)
  }

  function handleClick(
    event: SyntheticInputEvent<HTMLElement>,
    record: Claim,
    isItemSelected: boolean
  ): void {
    let newSelecteds = []
    if (isItemSelected) {
      newSelecteds = selectedClaims.filter((r: Claim) => r.id !== record.id)
    } else {
      newSelecteds = [...selectedClaims, record]
    }

    setSelectedClaims(newSelecteds)
    onSelectedCallback(newSelecteds)
  }

  function handleChange(event: SyntheticInputEvent<HTMLElement>, index: number): void {
    let newRecords = [...records]
    newRecords[index][event.target.name] =
      event.target.name === 'privateBillingAmount' ? Number(event.target.value) : event.target.value

    setClaims(newRecords)
  }

  function handleSubmit(): void {
    onEditingClaims(claims)
    handleNext()
  }

  function isSelected(record: Claim): boolean {
    return selectedClaims.some((r) => r.id === record.id)
  }

  return (
    <Grid container item spacing={3}>
      <Grid item>
        <Typography variant="h2">Select claims to include</Typography>
        <Typography>
          The private billing amounts shown are the advised rates from {ministryMapping[province]}.
        </Typography>
      </Grid>
      <Grid item>
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size="medium"
            aria-label="enhanced table"
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={records.length > 0 && selectedClaims.length === records.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                {headCells.map((headCell) => (
                  <TableCell
                    className={classes.cell}
                    key={headCell.id}
                    align={headCell.numeric ? 'right' : 'left'}
                    padding={headCell.disablePadding ? 'none' : 'default'}
                  >
                    {headCell.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record, index) => {
                const isItemSelected = isSelected(record)
                const labelId = `enhanced-table-checkbox-${index}`
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={record.id}
                    selected={isItemSelected}
                  >
                    <TableCell className={classes.cell} padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        inputProps={{ 'aria-labelledby': labelId }}
                        onClick={(event: SyntheticInputEvent<HTMLElement>) =>
                          handleClick(event, record, isItemSelected)
                        }
                      />
                    </TableCell>
                    <TableCell
                      className={classes.cell}
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                    >
                      {record.status}
                    </TableCell>
                    <TableCell className={classes.dateCell} align="right" disabled={false}>
                      {formatDate(record.dateOfService)}
                    </TableCell>
                    <TableCell className={classes.cell} align="right">
                      {record.code}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <TextField
                        defaultValue={record.description}
                        name="description"
                        onChange={(event: SyntheticInputEvent<HTMLElement>) =>
                          handleChange(event, index)
                        }
                      />
                    </TableCell>
                    <TableCell className={classes.cell} align="right">
                      <TextField
                        defaultValue={record.privateBillingAmount.toFixed(2)}
                        name="privateBillingAmount"
                        onChange={(event: SyntheticInputEvent<HTMLElement>) =>
                          handleChange(event, index)
                        }
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item container justify="flex-end" xs={12}>
        <CardActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Next
          </Button>
        </CardActions>
      </Grid>
    </Grid>
  )
}

export default ClaimsList
